import { useEffect, useState, useCallback } from 'react'
import {
  Sun, AlertTriangle, Target, TrendingUp,
  Bot, Shield, BarChart2, RefreshCw,
  Coffee, Globe, Calendar, Activity, Gauge,
  Bell, ChevronRight, Zap,
} from 'lucide-react'
import { clsx } from 'clsx'
import { useAgents } from '@/hooks/useAgents'
import { format } from 'date-fns'
import { getLatestBriefing, regenerateBriefing, type BriefingData, type LatestBriefingResponse } from '@/lib/api'

function fmt(n: number | null | undefined, prefix = '£') {
  if (n == null || !Number.isFinite(n)) return `${prefix}0`
  return `${prefix}${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function PriorityBadge({ index }: { index: number }) {
  const colors = [
    'bg-rose/20 text-rose-light border-rose/30',
    'bg-amber/20 text-amber-light border-amber/30',
    'bg-blue/20 text-blue-light border-blue/30',
  ]
  const color = index < 1 ? colors[0] : index < 3 ? colors[1] : colors[2]
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold border shrink-0 ${color}`}>
      {index + 1}
    </span>
  )
}

export default function Advisory() {
  const { analysis, analyzing, runAnalysis, agents, fetchStatus } = useAgents()
  const [briefing, setBriefing] = useState<BriefingData | null>(null)
  const [briefingLoading, setBriefingLoading] = useState(false)
  const [briefingRunning, setBriefingRunning] = useState(false)
  const [briefingNotRun, setBriefingNotRun] = useState(false)
  const [briefingNote, setBriefingNote] = useState<string | null>(null)

  // GET /latest serves the last STORED brief instantly — it never rebuilds
  // inline. status not_run means nothing generated yet (press Regenerate).
  const applyBriefing = useCallback((data: LatestBriefingResponse) => {
    setBriefingRunning(data.running ?? false)
    if (data.market_sessions) {
      setBriefing(data as BriefingData)
      setBriefingNotRun(false)
      setBriefingNote(null)
    } else {
      setBriefingNotRun(true)
      setBriefingNote(data.note ?? null)
    }
  }, [])

  const fetchBriefing = useCallback(async () => {
    setBriefingLoading(true)
    try {
      applyBriefing(await getLatestBriefing())
    } catch {
      // silently ignore — briefing is supplemental
    } finally {
      setBriefingLoading(false)
    }
  }, [applyBriefing])

  // Kick a background rebuild, then poll /latest until it lands. The old
  // stored brief keeps displaying while the new one is generated.
  const regenerateNow = useCallback(async () => {
    try {
      setBriefingRunning(true)
      await regenerateBriefing()
      for (let i = 0; i < 40; i++) {
        await new Promise<void>(resolve => setTimeout(resolve, 3000))
        const data = await getLatestBriefing()
        applyBriefing(data)
        if (!(data.running ?? false)) return
      }
      setBriefingRunning(false)
    } catch {
      setBriefingRunning(false)
    }
  }, [applyBriefing])

  useEffect(() => {
    runAnalysis()
    fetchStatus()
    fetchBriefing()
  }, []) // eslint-disable-line

  const now = new Date()
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber/20 border border-amber/30 flex items-center justify-center">
            <Sun size={20} className="text-amber" aria-hidden />
          </div>
          <div>
            <h1 className="page-title">{greeting}</h1>
            <p className="text-sm text-text-secondary">
              {analysis
                ? `Advisory generated ${format(new Date(analysis.timestamp), 'HH:mm, d MMM yyyy')}`
                : 'Loading advisory...'}
            </p>
          </div>
        </div>
        <button
          onClick={() => runAnalysis()}
          disabled={analyzing}
          className="btn-secondary text-xs gap-1.5"
          aria-label="Refresh advisory"
        >
          <RefreshCw size={13} className={analyzing ? 'animate-spin' : ''} aria-hidden />
          {analyzing ? 'Analysing...' : 'Refresh'}
        </button>
      </div>

      {/* ── Morning Briefing Panel ─────────────────────────────────────── */}
      {(briefing || briefingLoading || briefingNotRun) && (
        <div className="card p-5 border border-amber/20 bg-amber/5">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Coffee size={16} className="text-amber" aria-hidden />
              <h2 className="panel-title">
                Morning Briefing
                {briefing?.date_label ? (
                  <span className="ml-2 text-xs font-normal text-text-muted">
                    {briefing.date_label}
                  </span>
                ) : null}
                {briefing?.generated_at ? (
                  <span className="ml-2 text-xs font-normal text-text-muted">
                    as of {format(new Date(briefing.generated_at), 'HH:mm')}
                  </span>
                ) : null}
              </h2>
              {briefingRunning && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber/15 text-amber font-medium">
                  Regenerating in background…
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={regenerateNow}
              disabled={briefingRunning}
              className="btn-secondary text-xs gap-1"
              aria-label="Regenerate briefing"
            >
              <RefreshCw size={11} className={briefingRunning ? 'animate-spin' : ''} aria-hidden />
              {briefingRunning ? 'Regenerating…' : 'Regenerate'}
            </button>
          </div>

          {briefingLoading && !briefing && !briefingNotRun && (
            <div className="space-y-2 py-4">
              <div className="h-4 w-1/2 skeleton" />
              <div className="h-4 w-2/3 skeleton" />
              <p className="text-sm text-text-muted">Loading briefing...</p>
            </div>
          )}

          {briefingNotRun && !briefing && !briefingLoading && (
            <div className="flex flex-col items-start gap-2 py-2">
              <p className="text-sm text-text-muted">
                {briefingNote ?? 'No briefing generated yet — press Regenerate.'}
              </p>
              {!briefingRunning && (
                <button
                  type="button"
                  onClick={regenerateNow}
                  className="btn-secondary text-xs"
                >
                  Generate briefing
                </button>
              )}
            </div>
          )}

          {briefing && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">

              {/* Market sessions */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Globe size={12} className="text-text-muted" aria-hidden />
                  <span className="panel-title">
                    Market Sessions
                  </span>
                </div>
                <div className="space-y-1">
                  {briefing.market_sessions.map(s => (
                    <div key={s.name} className="flex items-center justify-between text-xs">
                      <span className="text-text-muted truncate pr-2">{s.name}</span>
                      <span className={`font-semibold shrink-0 ${
                        s.status === 'OPEN' ? 'text-emerald-400' : 'text-text-muted'
                      }`}>
                        {s.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Overnight moves */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Activity size={12} className="text-text-muted" aria-hidden />
                  <span className="panel-title">
                    Overnight Moves
                  </span>
                </div>
                {briefing.overnight_moves.length > 0 ? (
                  <div className="space-y-1">
                    {briefing.overnight_moves.map(m => (
                      <div key={m.ticker} className="flex items-center justify-between text-xs">
                        <span className="text-text-muted">{m.name}</span>
                        <span className={`font-mono font-semibold ${
                          m.change_pct >= 0 ? 'text-emerald-400' : 'text-rose-400'
                        }`}>
                          {(m.change_pct ?? 0) >= 0 ? '+' : ''}{(m.change_pct ?? 0).toFixed(2)}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-text-muted">Data unavailable</p>
                )}
              </div>

              {/* Portfolio + Fear & Greed */}
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <TrendingUp size={12} className="text-text-muted" aria-hidden />
                    <span className="panel-title">
                      Portfolio
                    </span>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Value</span>
                      <span className="font-mono text-text-primary">
                        £{briefing.portfolio.pot_value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Overnight P&L</span>
                      <span className={`font-mono font-semibold ${
                        briefing.portfolio.daily_pnl >= 0 ? 'text-emerald-400' : 'text-rose-400'
                      }`}>
                        {briefing.portfolio.daily_pnl >= 0 ? '+' : ''}
                        £{briefing.portfolio.daily_pnl.toLocaleString('en-GB', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Fear & Greed */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Gauge size={12} className="text-text-muted" aria-hidden />
                    <span className="panel-title">
                      Fear & Greed
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-surface-hover rounded-full overflow-hidden">
                      <div
                        className={[
                          'h-full rounded-full transition-all progress-bar',
                          briefing.fear_greed.value > 60
                            ? 'progress-bar-emerald'
                            : briefing.fear_greed.value < 40
                            ? 'progress-bar-rose'
                            : 'progress-bar-amber',
                          `factor-bar-${Math.round(briefing.fear_greed.value / 5) * 5}`,
                        ].join(' ')}
                      />
                    </div>
                    <span className="text-xs font-mono text-text-secondary shrink-0">
                      {briefing.fear_greed.value}
                    </span>
                    <span className="text-xs text-text-muted shrink-0">
                      {briefing.fear_greed.label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Economic events */}
              {briefing.economic_events.length > 0 && (
                <div className="md:col-span-2">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Calendar size={12} className="text-text-muted" aria-hidden />
                    <span className="panel-title">
                      Key Events Today
                    </span>
                  </div>
                  <div className="space-y-1">
                    {briefing.economic_events.slice(0, 5).map((ev, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          ev.impact === 'high' || ev.impact === '3'
                            ? 'bg-rose-500/20 text-rose-400'
                            : 'bg-amber/20 text-amber'
                        }`}>
                          {ev.impact === 'high' || ev.impact === '3' ? 'HIGH' : 'MED'}
                        </span>
                        <span className="text-text-muted shrink-0">{ev.time.slice(11, 16) || ev.time}</span>
                        <span className="text-text-muted font-mono shrink-0">{ev.country}</span>
                        <span className="text-text-secondary truncate">{ev.event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top recommendations */}
              {briefing.top_recommendations.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <ChevronRight size={12} className="text-text-muted" aria-hidden />
                    <span className="panel-title">
                      Top Agent Signals
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {briefing.top_recommendations.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className={`shrink-0 px-1.5 py-0.5 rounded font-semibold ${
                          r.action === 'BUY'
                            ? 'bg-emerald-500/20 text-emerald-400'
                            : r.action === 'SELL'
                            ? 'bg-rose-500/20 text-rose-400'
                            : 'bg-amber/20 text-amber'
                        }`}>
                          {r.action}
                        </span>
                        {r.ticker && (
                          <span className="font-mono font-semibold text-text-primary">{r.ticker}</span>
                        )}
                        <span className="text-text-muted">{r.confidence}%</span>
                        <span className="text-text-muted truncate">
                          {r.agent_name || r.agent_id || ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Watchlist alerts */}
              {briefing.watchlist_alerts.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Bell size={12} className="text-amber" aria-hidden />
                    <span className="panel-title text-amber">
                      Watchlist Alerts
                    </span>
                  </div>
                  <div className="space-y-1">
                    {briefing.watchlist_alerts.slice(0, 3).map((a, i) => (
                      <div key={i} className="text-xs text-amber/80">
                        {String(a.ticker ?? '???')} — {String(a.alert_type ?? '')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Book catalysts — FDA/earnings/halts on what the user holds */}
              {briefing.book_catalysts && briefing.book_catalysts.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Zap size={12} className="text-blue-light" aria-hidden />
                    <span className="panel-title text-blue-light">
                      Catalysts On Your Book
                    </span>
                    <span className="text-[10px] text-text-muted">({briefing.book_catalysts.length})</span>
                  </div>
                  <div className="space-y-1">
                    {briefing.book_catalysts.slice(0, 5).map((c, i) => (
                      <a
                        key={i}
                        href={`/app/stock/${encodeURIComponent(c.ticker)}`}
                        className="flex items-start gap-2 text-xs hover:bg-surface-hover/40 -mx-1 px-1 py-0.5 rounded transition"
                      >
                        <span className={clsx(
                          'shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-bold',
                          c.sentiment >= 0.1 ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                            : c.sentiment <= -0.1 ? 'bg-rose-500/15 text-rose-300 border-rose-500/30'
                            : 'bg-surface-card text-text-muted border-border',
                        )}>
                          {c.category}
                        </span>
                        <span className="font-bold text-blue-light shrink-0">{c.ticker}</span>
                        <span className="text-text-secondary truncate">{c.summary}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {analyzing && !analysis && (
        <div className="card p-12 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 w-full max-w-md">
            <div className="h-6 w-full skeleton" />
            <div className="h-6 w-3/4 skeleton" />
            <p className="text-sm text-text-muted">Generating morning advisory...</p>
          </div>
        </div>
      )}

      {analysis && (
        <>
          {/* Top KPIs row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger-children">
            {/* Market regime */}
            <div className="stat-card card-hover">
              <p className="stat-label">Market Regime</p>
              <p className="stat-value text-lg">{analysis.market.regime}</p>
              <p className="text-xs text-text-muted">{analysis.market.status}</p>
            </div>

            {/* Risk score */}
            <div className="stat-card">
              <p className="stat-label">Risk Score</p>
              <p className="stat-hero text-gradient">
                {(analysis.risk?.overall_risk_score ?? 0).toFixed(1)}
                <span className="text-sm text-text-muted font-normal">/10</span>
              </p>
              <p className={`text-xs ${analysis.risk.overall_risk_score > 6 ? 'text-rose-light' : analysis.risk.overall_risk_score > 3 ? 'text-amber' : 'text-emerald-light'}`}>
                {analysis.risk.risk_label}
              </p>
            </div>

            {/* Opportunity */}
            <div className="stat-card">
              <p className="stat-label">Opportunity Score</p>
              <p className="stat-value text-lg">
                {analysis.market.opportunity_score}
                <span className="text-sm text-text-muted font-normal">/10</span>
              </p>
              <p className="text-xs text-text-muted">{analysis.market.recommendation}</p>
            </div>

            {/* Goals */}
            <div className="stat-card">
              <p className="stat-label">Goals on Track</p>
              <p className="stat-value text-lg">
                {analysis.goals.goals.filter(g => g.on_track).length}/{analysis.goals.goals.length}
              </p>
              <p className={`text-xs ${analysis.goals.overall_on_track ? 'text-emerald-light' : 'text-amber'}`}>
                {analysis.goals.overall_on_track ? 'All on track' : 'Action needed'}
              </p>
            </div>
          </div>

          {/* Main content */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Recommendations - priority sorted */}
            <div className="card p-5 lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp size={16} className="text-blue-light" aria-hidden />
                <h2 className="panel-title">Priority Recommendations</h2>
              </div>
              {analysis.recommendations.length ? (
                <ul className="space-y-3" aria-label="Priority recommendations">
                  {analysis.recommendations.map((rec, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <PriorityBadge index={i} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-secondary leading-relaxed">{rec}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          {i === 0 && <span className="badge-red">Urgent</span>}
                          {i >= 1 && i < 3 && <span className="badge-amber">Important</span>}
                          {i >= 3 && <span className="badge-blue">Monitor</span>}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-text-muted">No recommendations available.</p>
              )}
            </div>

            {/* ARIA summary */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Bot size={16} className="text-purple-light" aria-hidden />
                <h2 className="panel-title">ARIA Summary</h2>
              </div>
              <p className="text-sm text-text-secondary leading-relaxed mb-4">
                {analysis.aria_summary}
              </p>
              <div className="border-t border-border pt-3 space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-text-muted">Sentiment</span>
                  <span className={`badge ${
                    analysis.behavioral.sentiment === 'bullish' ? 'badge-green' :
                    analysis.behavioral.sentiment === 'anxious' ? 'badge-red' : 'badge-blue'
                  }`}>
                    {analysis.behavioral.sentiment}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-text-muted">Stress</span>
                  <span className="text-text-secondary">{Math.round(analysis.behavioral.stress_level * 100)}%</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-text-muted">Confidence</span>
                  <span className="text-text-secondary">{Math.round(analysis.behavioral.confidence_level * 100)}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Goal probabilities */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Target size={16} className="text-emerald" aria-hidden />
                <h2 className="panel-title">Goal Probabilities</h2>
              </div>
              <ul className="space-y-3" aria-label="Goal probabilities">
                {analysis.goals.goals.map(goal => (
                  <li key={goal.goal_id} className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-text-secondary capitalize">{goal.goal_type.replace('_', ' ')}</span>
                      <span className={goal.on_track ? 'text-emerald-light' : 'text-amber'}>
                        {Math.round(goal.probability * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden">
                      <div
                        className={[
                          'h-full rounded-full transition-all progress-bar',
                          goal.on_track ? 'progress-bar-emerald' : 'progress-bar-amber',
                          `factor-bar-${Math.round(goal.probability * 100 / 5) * 5}`,
                        ].join(' ')}
                      />
                    </div>
                    <p className="text-xs text-text-muted">{goal.narrative}</p>
                  </li>
                ))}
              </ul>
            </div>

            {/* Risk warnings */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <AlertTriangle size={16} className="text-amber" aria-hidden />
                <h2 className="panel-title">Risk Warnings</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">VaR (95% daily)</span>
                  <span className="text-text-secondary font-mono">{fmt(analysis.risk.var_95_daily)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">VaR (99% daily)</span>
                  <span className="text-text-secondary font-mono">{fmt(analysis.risk.var_99_daily)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">Max Drawdown</span>
                  <span className="text-text-secondary font-mono">{((analysis.risk?.max_drawdown ?? 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-muted">Sharpe Ratio</span>
                  <span className="text-text-secondary font-mono">{(analysis.risk?.sharpe_ratio ?? 0).toFixed(2)}</span>
                </div>
                {analysis.risk.position_warnings.length > 0 && (
                  <div className="border-t border-border pt-3 space-y-2">
                    {analysis.risk.position_warnings.map((w, i) => (
                      <div key={i} className={`text-xs p-2 rounded-lg ${
                        w.severity === 'critical' ? 'bg-rose/10 text-rose-light' :
                        w.severity === 'warning' ? 'bg-amber/10 text-amber' : 'bg-blue/10 text-blue-light'
                      }`}>
                        {w.message}
                      </div>
                    ))}
                  </div>
                )}
                {analysis.risk.sector_concentration_warnings.length > 0 && (
                  <div className="border-t border-border pt-3 space-y-1">
                    {analysis.risk.sector_concentration_warnings.map((w, i) => (
                      <p key={i} className="text-xs text-amber">{w}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Contributing agents */}
            <div className="card p-5">
              <div className="flex items-center gap-2 mb-4">
                <Shield size={16} className="text-blue-light" aria-hidden />
                <h2 className="panel-title">Contributing Agents</h2>
              </div>
              {agents.length > 0 ? (
                <ul className="space-y-2.5" aria-label="Contributing agents">
                  {agents.map(agent => (
                    <li key={agent.id} className="flex items-center gap-3">
                      <span className={`status-dot ${
                        agent.status === 'active' ? 'status-active' :
                        agent.status === 'error' ? 'status-error' : 'status-idle'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text-primary truncate">{agent.name}</p>
                        <p className="text-xs text-text-muted truncate">{agent.current_task}</p>
                      </div>
                      <span className="badge-green text-[10px]">{agent.performance}%</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-2.5">
                  {[
                    { name: 'Market Agent', role: 'Market regime & indices' },
                    { name: 'Risk Agent', role: 'Portfolio risk assessment' },
                    { name: 'Behavioral Agent', role: 'Investor behavioral analysis' },
                    { name: 'Goals Agent', role: 'Goal tracking & projections' },
                    { name: 'Optimization Agent', role: 'Portfolio optimization' },
                  ].map(a => (
                    <div key={a.name} className="flex items-center gap-3">
                      <BarChart2 size={14} className="text-text-muted shrink-0" aria-hidden />
                      <div>
                        <p className="text-sm text-text-secondary">{a.name}</p>
                        <p className="text-xs text-text-muted">{a.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Market news */}
          {analysis.market.news.length > 0 && (
            <div className="card p-5">
              <h2 className="panel-title mb-4">Market News</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {analysis.market.news.slice(0, 6).map(item => (
                  <div key={item.id} className="p-3 rounded-lg bg-surface-hover border border-border">
                    <p className="text-sm text-text-secondary leading-snug mb-2">{item.headline}</p>
                    <div className="flex items-center justify-between text-xs text-text-muted">
                      <span>{item.source}</span>
                      <span className={`badge ${
                        item.sentiment === 'positive' ? 'badge-green' :
                        item.sentiment === 'negative' ? 'badge-red' : 'badge-blue'
                      }`}>
                        {item.sentiment}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
